import React from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  LabelList,
} from "recharts";
import ManIcon from "@mui/icons-material/Man";
import WomanIcon from "@mui/icons-material/Woman";

export default function AgeGenderDistributionChart({ data, summary }) {
  const femaleSummary = summary.find((item) => item.gender === "F");
  const maleSummary = summary.find((item) => item.gender === "M");

  const renderCustomizedLabel = (props) => {
    const { x, y, width, height, value, index } = props;
    const item = data[index];
    const total = (item.male || 0) + (item.female || 0);

    return (
      <text
        x={x + width + 5}
        y={y + height / 1.8}
        fill="black"
        textAnchor="start"
        dominantBaseline="middle"
        fontWeight="bold"
        fontSize={14}
      >
        {total.toLocaleString()}
      </text>
    );
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        gap: "10px",
      }}
    >
      <div style={{ flex: 2 }}>
        <ResponsiveContainer width="100%" height={500}>
          <BarChart
            data={data}
            layout="vertical"
            margin={{ right: 40, left: 20, bottom: 10 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis type="number" />
            <YAxis type="category" dataKey="ageGroup" />
            <Tooltip
              formatter={(value, label) => [
                `${value?.toLocaleString()} ${
                  value === 1 ? "Teacher" : "Teachers"
                }`,
                label,
              ]}
            />
            <Legend />
            <Bar
              dataKey="female"
              fill="hsl(var(--chart-2))"
              name="Female"
              stackId="gender"
            />
            <Bar
              dataKey="male"
              fill="hsl(var(--chart-1))"
              name="Male"
              stackId="gender"
            >
              <LabelList content={renderCustomizedLabel} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div
        style={{
          flex: 1,
          fontSize: "14px",
          display: "flex",
          flexDirection: "row",
          justifyContent: "center",
          marginLeft: "30px",
          marginRight: "30px",
          gap: "20px",
        }}
      >
        <div style={{ textAlign: "center", width: "100%" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <WomanIcon style={{ fontSize: 40, color: "hsl(var(--chart-2))" }} />
            <div>
              <div
                style={{
                  fontWeight: "bold",
                  fontSize: "18px",
                  color: "hsl(var(--chart-2))",
                }}
              >
                {femaleSummary?.total.toLocaleString()}
              </div>
              <span>Female teachers</span>
            </div>
          </div>
          <table
            style={{
              width: "100%",
              marginTop: "10px",
              borderCollapse: "collapse",
            }}
          >
            <thead>
              <tr>
                <th
                  style={{
                    borderBottom: "1px solid #ccc",
                    padding: "5px 0px 5px",
                    background: "rgb(237, 108, 2)",
                    color: "white",
                  }}
                >
                  Average
                </th>
                <th
                  style={{
                    borderBottom: "1px solid #ccc",
                    padding: "5px 0px 5px",
                    background: "rgb(237, 108, 2)",
                    color: "white",
                  }}
                >
                  Min
                </th>
                <th
                  style={{
                    borderBottom: "1px solid #ccc",
                    padding: "5px 0px 5px",
                    background: "rgb(237, 108, 2)",
                    color: "white",
                  }}
                >
                  Max
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td
                  style={{
                    padding: "5px 0px 5px",
                    background: "rgb(236, 202, 174)",
                    fontWeight: "bold",
                  }}
                >
                  {femaleSummary?.averageAge}
                </td>
                <td
                  style={{
                    padding: "5px 0px 5px",
                    background: "rgb(236, 202, 174)",
                    fontWeight: "bold",
                  }}
                >
                  {femaleSummary?.minAge}
                </td>
                <td
                  style={{
                    padding: "5px 0px 5px",
                    background: "rgb(236, 202, 174)",
                    fontWeight: "bold",
                  }}
                >
                  {femaleSummary?.maxAge}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div style={{ textAlign: "center", width: "100%" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <ManIcon style={{ fontSize: 40, color: "hsl(var(--chart-1))" }} />
            <div>
              <div
                style={{
                  fontWeight: "bold",
                  fontSize: "18px",
                  color: "hsl(var(--chart-1))",
                }}
              >
                {maleSummary?.total.toLocaleString()}
              </div>
              <span>Male teachers</span>
            </div>
          </div>
          <table
            style={{
              width: "100%",
              marginTop: "10px",
              borderCollapse: "collapse",
            }}
          >
            <thead>
              <tr>
                <th
                  style={{
                    borderBottom: "1px solid #ccc",
                    padding: "5px 0px 5px",
                    background: "rgb(1, 81, 152)",
                    color: "white",
                  }}
                >
                  Average
                </th>
                <th
                  style={{
                    borderBottom: "1px solid #ccc",
                    padding: "5px 0px 5px",
                    background: "rgb(1, 81, 152)",
                    color: "white",
                  }}
                >
                  Min
                </th>
                <th
                  style={{
                    borderBottom: "1px solid #ccc",
                    padding: "5px 0px 5px",
                    background: "rgb(1, 81, 152)",
                    color: "white",
                  }}
                >
                  Max
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td
                  style={{
                    padding: "5px 0px 5px",
                    background: "rgb(197, 221, 241)",
                    fontWeight: "bold",
                  }}
                >
                  {maleSummary?.averageAge}
                </td>
                <td
                  style={{
                    padding: "5px 0px 5px",
                    background: "rgb(197, 221, 241)",
                    fontWeight: "bold",
                  }}
                >
                  {maleSummary?.minAge}
                </td>
                <td
                  style={{
                    padding: "5px 0px 5px",
                    background: "rgb(197, 221, 241)",
                    fontWeight: "bold",
                  }}
                >
                  {maleSummary?.maxAge}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
